import {
  HStack,
  Tooltip,
  VStack,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuOptionGroup,
  MenuItemOption,
  Button,
  Spinner,
  Text,
  Badge,
} from '@chakra-ui/react';
import {
  faDrawPolygon,
  faMagicWandSparkles,
  faInfoCircle,
  faChevronDown,
  faCheck,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from 'urql';

import SparkelIcon from 'src/components/common/icon/SparkelIcon';
import { ReactComponent as RectangleIcon } from 'src/assets/icons/draw-rectangle.svg';
import { DrawingMode } from 'src/components/viewers/SheetsViewerV2/autodesk/tools/ShapesDrawingTool';
import { GetSparkelAiAnalysisByProjectDocument } from 'src/gql/graphql';

interface ProtanRoofOutlineSubToolbarProps {
  projectId: string;
  roofOutlineData: {
    energyRequirements: string;
    fireRequirements: string;
    substrate: string;
    location: string;
    buildingHeight: string;
    windLoad: string;
  };
  setRoofOutlineData: React.Dispatch<
    React.SetStateAction<{
      energyRequirements: string;
      fireRequirements: string;
      substrate: string;
      location: string;
      buildingHeight: string;
      windLoad: string;
    }>
  >;
  isEditing: boolean;
  roofOutlineSubMode:
    | DrawingMode.Polygon
    | DrawingMode.Rectangle
    | DrawingMode.MagicPolygon;
  setRoofOutlineSubMode: (
    mode: DrawingMode.Polygon | DrawingMode.Rectangle | DrawingMode.MagicPolygon
  ) => void;
  setDrawingMode: (mode: DrawingMode) => void;
  fireRequirements: string;
  backgroundColor: string;
  borderColor: string;
  iconColor: string;
  activeBackgroundColor: string;
  activeIconColor: string;
  onSubmit: () => void;
  isSaveDisabled: boolean;
  isSaveLoading: boolean;
}

// eslint-disable-next-line complexity
export function ProtanRoofOutlineSubToolbar({
  projectId,
  roofOutlineData,
  setRoofOutlineData,
  isEditing,
  roofOutlineSubMode,
  setRoofOutlineSubMode,
  setDrawingMode,
  fireRequirements,
  backgroundColor,
  borderColor,
  iconColor,
  activeBackgroundColor,
  activeIconColor,
  onSubmit,
  isSaveDisabled,
  isSaveLoading,
}: ProtanRoofOutlineSubToolbarProps) {
  const { t } = useTranslation(['project', 'protan']);

  // Query for AI analysis data
  const [{ data: aiData }, reexecuteQuery] = useQuery({
    query: GetSparkelAiAnalysisByProjectDocument,
    variables: { projectId },
  });

  // Track which fields have been manually changed by the user
  const [manuallyChangedFields, setManuallyChangedFields] = useState<
    Set<string>
  >(new Set());

  // Track if we're actively polling for AI results
  const [isPolling, setIsPolling] = useState(false);

  // Create AI labels mapping
  const aiLabels = aiData?.project?.sparkelAiAnalyses.map((analysis) => {
    return { key: analysis.label, value: analysis.value };
  });

  // Poll for AI analysis results every 3 seconds until we get them
  useEffect(() => {
    if (!aiData?.project?.sparkelAiAnalyses.length) {
      setIsPolling(true);
      const intervalId = setInterval(async () => {
        reexecuteQuery({ requestPolicy: 'network-only' });

        if (aiData?.project?.sparkelAiAnalyses.length) {
          clearInterval(intervalId);
          setIsPolling(false);
        }
      }, 3000); // Poll every 3 seconds

      return () => {
        clearInterval(intervalId);
        setIsPolling(false);
      };
    } else {
      setIsPolling(false);
    }
  }, [aiData?.project?.sparkelAiAnalyses.length, reexecuteQuery]);

  // Auto-populate fields with AI results when they arrive (only if not manually changed)
  useEffect(() => {
    // When editing an existing shape, never override initial values with AI
    if (isEditing) return;
    if (aiLabels && aiLabels.length > 0) {
      const keyMapping: { [key: string]: string } = {
        energyRequirements: 'Energy requirements',
        fireRequirements: 'Fire requirements',
        substrate: 'Substrate',
        location: 'Location',
        buildingHeight: 'Building height',
        windLoad: 'Qₚ (N/m²)',
      };

      // Define valid options for each select field
      const validOptions: { [key: string]: string[] } = {
        energyRequirements: ['0.09', '0.10', '0.12', '0.13', '0.18'],
        fireRequirements: ['Non-Combustible', 'Combustible'],
        substrate: ['Steel', 'OSB/Plywood', 'Concrete'],
        location: ['Sør', 'Nord', 'Øst', 'Vest'],
        // buildingHeight and windLoad are text inputs, so no validation needed
      };

      Object.entries(keyMapping).forEach(([fieldKey, aiKey]) => {
        // Only auto-populate if the field hasn't been manually changed
        if (!manuallyChangedFields.has(fieldKey)) {
          const aiLabel = aiLabels.find((label) => label.key === aiKey);
          if (aiLabel) {
            try {
              const parsedValue = JSON.parse(aiLabel.value);
              const aiValue = parsedValue.parameter || aiLabel.value;

              // For select fields, validate that the AI value is a valid option
              const validOptionsForField = validOptions[fieldKey];
              if (validOptionsForField) {
                // This is a select field - only update if AI value is valid
                if (validOptionsForField.includes(aiValue)) {
                  setRoofOutlineData((prev) => ({
                    ...prev,
                    [fieldKey]: aiValue,
                  }));
                }
              } else {
                // This is a text input field - update directly
                setRoofOutlineData((prev) => ({
                  ...prev,
                  [fieldKey]: aiValue,
                }));
              }
            } catch (error) {
              console.warn('Failed to parse AI value for', aiKey, ':', error);
            }
          }
        }
      });
    }
  }, [aiLabels, manuallyChangedFields, setRoofOutlineData, isEditing]);

  // If editing, treat all fields as manually changed to preserve existing values
  useEffect(() => {
    if (isEditing) {
      setManuallyChangedFields(
        new Set([
          'energyRequirements',
          'fireRequirements',
          'substrate',
          'location',
          'buildingHeight',
          'windLoad',
        ])
      );
    }
  }, [isEditing]);

  // Helper function to get AI result for a given key
  const getAiResultForKey = useCallback(
    (key: string) => {
      // Map our internal keys to the actual AI analysis label names
      const keyMapping: { [key: string]: string } = {
        energyRequirements: 'Energy requirements',
        fireRequirements: 'Fire requirements',
        substrate: 'Substrate',
        location: 'Location',
        buildingHeight: 'Building height', // This might not exist in AI data
        windLoad: 'Qₚ (N/m²)', // This is the wind load parameter
      };

      const actualKey = keyMapping[key];
      if (!actualKey) return null;

      const aiLabel = aiLabels?.find((label) => label.key === actualKey);
      if (!aiLabel) return null;

      // Parse the JSON value to get the parameter
      try {
        const parsedValue = JSON.parse(aiLabel.value);
        return parsedValue.parameter || aiLabel.value;
      } catch (error) {
        console.warn('Failed to parse AI value for', actualKey, ':', error);
        return aiLabel.value;
      }
    },
    [aiLabels]
  );

  // Helper to render AI info icon with tooltip
  const renderAiInfo = useCallback(
    (aiKey: string) => {
      const aiResult = getAiResultForKey(aiKey);
      if (!aiResult) return null;

      return (
        <Tooltip label={aiResult}>
          <SparkelIcon
            icon={faInfoCircle}
            color={'purple.400'}
            style={{ marginLeft: '4px', cursor: 'pointer' }}
          />
        </Tooltip>
      );
    },
    [getAiResultForKey]
  );

  return (
    <VStack
      position="absolute"
      bottom="100%"
      left={32}
      transform="translateX(-50%)"
      backgroundColor={backgroundColor}
      rounded={50}
      px={2}
      py={1}
      borderColor={borderColor}
      borderRadius={'lg'}
      borderWidth="1px"
      width={'fit-content'}
      zIndex={10}
      boxShadow="md"
      spacing={1}
      mb={1}
    >
      <Text fontSize="md" color="gray.50" fontWeight="bold">
        {t('protan:new-roof', 'New Roof')}
      </Text>
      <VStack spacing={1} width="100%">
        {/* Loading indicator when polling for AI results */}
        {isPolling && (
          <HStack spacing={2} width="100%" justify="center" py={1}>
            <Spinner size="xs" color="purple.400" />
            <Text fontSize="xs" color="gray.300">
              {t('protan:alert.ai-analysis.title')}
            </Text>
          </HStack>
        )}
        <HStack spacing={1} width="100%">
          <Text fontSize="xs" color="gray.300" minW={110} textAlign="right">
            {t('protan:label.energy-requirements', 'Energy requirements')}
          </Text>
          <Menu>
            <MenuButton
              as={Button}
              size="xs"
              variant="filled"
              borderRadius={'md'}
              borderColor={borderColor}
              backgroundColor={borderColor}
              rightIcon={<SparkelIcon icon={faChevronDown} color={iconColor} />}
              color={
                getAiResultForKey('energyRequirements') ===
                roofOutlineData.energyRequirements
                  ? 'purple.400'
                  : iconColor
              }
              _hover={{ backgroundColor: 'blackAlpha.500' }}
              flex={1}
              textAlign="left"
            >
              {roofOutlineData.energyRequirements || 'Energy Requirements'}
            </MenuButton>
            <MenuList>
              <MenuOptionGroup
                type="radio"
                value={roofOutlineData.energyRequirements}
                onChange={(value) => {
                  setRoofOutlineData((prev) => ({
                    ...prev,
                    energyRequirements: value as string,
                  }));
                  setManuallyChangedFields(
                    (prev) => new Set([...prev, 'energyRequirements'])
                  );
                }}
              >
                <MenuItemOption
                  value="0.09"
                  isDisabled={fireRequirements === 'Combustible'}
                  color={
                    getAiResultForKey('energyRequirements') === '0.09'
                      ? 'purple.400'
                      : undefined
                  }
                >
                  <HStack justify="space-between" width="100%">
                    <Text>{t('protan:energy-requirements.0.09')}</Text>
                    {getAiResultForKey('energyRequirements') === '0.09' && (
                      <Badge
                        variant="subtle"
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        AI
                      </Badge>
                    )}
                  </HStack>
                </MenuItemOption>
                <MenuItemOption
                  value="0.10"
                  color={
                    getAiResultForKey('energyRequirements') === '0.10'
                      ? 'purple.400'
                      : undefined
                  }
                >
                  <HStack justify="space-between" width="100%">
                    <Text>{t('protan:energy-requirements.0.10')}</Text>
                    {getAiResultForKey('energyRequirements') === '0.10' && (
                      <Badge
                        variant="subtle"
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        AI
                      </Badge>
                    )}
                  </HStack>
                </MenuItemOption>
                <MenuItemOption
                  value="0.12"
                  color={
                    getAiResultForKey('energyRequirements') === '0.12'
                      ? 'purple.400'
                      : undefined
                  }
                >
                  <HStack justify="space-between" width="100%">
                    <Text>{t('protan:energy-requirements.0.12')}</Text>
                    {getAiResultForKey('energyRequirements') === '0.12' && (
                      <Badge
                        variant="subtle"
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        AI
                      </Badge>
                    )}
                  </HStack>
                </MenuItemOption>
                <MenuItemOption
                  value="0.13"
                  color={
                    getAiResultForKey('energyRequirements') === '0.13'
                      ? 'purple.400'
                      : undefined
                  }
                >
                  <HStack justify="space-between" width="100%">
                    <Text>{t('protan:energy-requirements.0.13')}</Text>
                    {getAiResultForKey('energyRequirements') === '0.13' && (
                      <Badge
                        variant="subtle"
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        AI
                      </Badge>
                    )}
                  </HStack>
                </MenuItemOption>
                <MenuItemOption
                  value="0.18"
                  color={
                    getAiResultForKey('energyRequirements') === '0.18'
                      ? 'purple.400'
                      : undefined
                  }
                >
                  <HStack justify="space-between" width="100%">
                    <Text>{t('protan:energy-requirements.0.18')}</Text>
                    {getAiResultForKey('energyRequirements') === '0.18' && (
                      <Badge
                        variant="subtle"
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        AI
                      </Badge>
                    )}
                  </HStack>
                </MenuItemOption>
              </MenuOptionGroup>
            </MenuList>
          </Menu>
          {renderAiInfo('energyRequirements')}
        </HStack>
        <HStack spacing={1} width="100%">
          <Text fontSize="xs" color="gray.300" minW={110} textAlign="right">
            {t('protan:label.fire-requirements', 'Fire requirements')}
          </Text>
          <Menu>
            <MenuButton
              as={Button}
              size="xs"
              variant="filled"
              borderRadius={'md'}
              borderColor={borderColor}
              backgroundColor={borderColor}
              rightIcon={<SparkelIcon icon={faChevronDown} color={iconColor} />}
              color={
                getAiResultForKey('fireRequirements') ===
                roofOutlineData.fireRequirements
                  ? 'purple.400'
                  : iconColor
              }
              _hover={{ backgroundColor: 'blackAlpha.500' }}
              flex={1}
              textAlign="left"
            >
              {t(
                'protan:fire-requirements.' +
                  roofOutlineData.fireRequirements.toLowerCase()
              ) || t('protan:fire-requirements.combustible')}
            </MenuButton>
            <MenuList>
              <MenuOptionGroup
                type="radio"
                value={roofOutlineData.fireRequirements}
                onChange={(value) => {
                  setRoofOutlineData((prev) => ({
                    ...prev,
                    fireRequirements: value as string,
                  }));
                  setManuallyChangedFields(
                    (prev) => new Set([...prev, 'fireRequirements'])
                  );
                }}
              >
                <MenuItemOption
                  value="Non-Combustible"
                  color={
                    getAiResultForKey('fireRequirements') === 'Non-Combustible'
                      ? 'purple.400'
                      : undefined
                  }
                >
                  <HStack justify="space-between" width="100%">
                    <Text>{t('protan:fire-requirements.non-combustible')}</Text>
                    {getAiResultForKey('fireRequirements') ===
                      'Non-Combustible' && (
                      <Badge
                        variant="subtle"
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        AI
                      </Badge>
                    )}
                  </HStack>
                </MenuItemOption>
                <MenuItemOption
                  value="Combustible"
                  color={
                    getAiResultForKey('fireRequirements') === 'Combustible'
                      ? 'purple.400'
                      : undefined
                  }
                >
                  <HStack justify="space-between" width="100%">
                    <Text>{t('protan:fire-requirements.combustible')}</Text>
                    {getAiResultForKey('fireRequirements') ===
                      'Combustible' && (
                      <Badge
                        variant="subtle"
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        AI
                      </Badge>
                    )}
                  </HStack>
                </MenuItemOption>
              </MenuOptionGroup>
            </MenuList>
          </Menu>
          {renderAiInfo('fireRequirements')}
        </HStack>
        <HStack spacing={1} width="100%">
          <Text fontSize="xs" color="gray.300" minW={110} textAlign="right">
            {t('protan:Substrate', 'Substrate')}
          </Text>
          <Menu>
            <MenuButton
              as={Button}
              size="xs"
              variant="filled"
              borderRadius={'md'}
              borderColor={borderColor}
              backgroundColor={borderColor}
              rightIcon={<SparkelIcon icon={faChevronDown} color={iconColor} />}
              color={
                getAiResultForKey('substrate') === roofOutlineData.substrate
                  ? 'purple.400'
                  : iconColor
              }
              _hover={{ backgroundColor: 'blackAlpha.500' }}
              flex={1}
              textAlign="left"
            >
              {t(
                'protan:substrate.' + roofOutlineData.substrate.toLowerCase()
              ) || t('protan:substrate.steel')}
            </MenuButton>
            <MenuList>
              <MenuOptionGroup
                type="radio"
                value={roofOutlineData.substrate}
                onChange={(value) => {
                  setRoofOutlineData((prev) => ({
                    ...prev,
                    substrate: value as string,
                  }));
                  setManuallyChangedFields(
                    (prev) => new Set([...prev, 'substrate'])
                  );
                }}
              >
                <MenuItemOption
                  value="Steel"
                  color={
                    getAiResultForKey('substrate') === 'Steel'
                      ? 'purple.400'
                      : undefined
                  }
                >
                  <HStack justify="space-between" width="100%">
                    <Text>{t('protan:substrate.steel')}</Text>
                    {getAiResultForKey('substrate') === 'Steel' && (
                      <Badge
                        variant="subtle"
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        AI
                      </Badge>
                    )}
                  </HStack>
                </MenuItemOption>
                <MenuItemOption
                  value="OSB/Plywood"
                  color={
                    getAiResultForKey('substrate') === 'OSB/Plywood'
                      ? 'purple.400'
                      : undefined
                  }
                >
                  <HStack justify="space-between" width="100%">
                    <Text>{t('protan:substrate.osb-plywood')}</Text>
                    {getAiResultForKey('substrate') === 'OSB/Plywood' && (
                      <Badge
                        variant="subtle"
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        AI
                      </Badge>
                    )}
                  </HStack>
                </MenuItemOption>
                <MenuItemOption
                  value="Concrete"
                  color={
                    getAiResultForKey('substrate') === 'Concrete'
                      ? 'purple.400'
                      : undefined
                  }
                >
                  <HStack justify="space-between" width="100%">
                    <Text>{t('protan:substrate.concrete')}</Text>
                    {getAiResultForKey('substrate') === 'Concrete' && (
                      <Badge
                        variant="subtle"
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        AI
                      </Badge>
                    )}
                  </HStack>
                </MenuItemOption>
              </MenuOptionGroup>
            </MenuList>
          </Menu>
          {renderAiInfo('substrate')}
        </HStack>
        <HStack spacing={1} width="100%">
          <Text fontSize="xs" color="gray.300" minW={110} textAlign="right">
            {t('protan:label.location', 'Location')}
          </Text>
          <Menu>
            <MenuButton
              as={Button}
              size="xs"
              variant="filled"
              borderRadius={'md'}
              borderColor={borderColor}
              backgroundColor={borderColor}
              rightIcon={<SparkelIcon icon={faChevronDown} color={iconColor} />}
              color={
                getAiResultForKey('location') === roofOutlineData.location
                  ? 'purple.400'
                  : iconColor
              }
              _hover={{ backgroundColor: 'blackAlpha.500' }}
              flex={1}
              textAlign="left"
            >
              {roofOutlineData.location || 'Location'}
            </MenuButton>
            <MenuList>
              <MenuOptionGroup
                type="radio"
                value={roofOutlineData.location}
                onChange={(value) => {
                  setRoofOutlineData((prev) => ({
                    ...prev,
                    location: value as string,
                  }));
                  setManuallyChangedFields(
                    (prev) => new Set([...prev, 'location'])
                  );
                }}
              >
                <MenuItemOption
                  value="Sør"
                  color={
                    getAiResultForKey('location') === 'Sør'
                      ? 'purple.400'
                      : undefined
                  }
                >
                  <HStack justify="space-between" width="100%">
                    <Text>{t('protan:location.sor')}</Text>
                    {getAiResultForKey('location') === 'Sør' && (
                      <Badge
                        variant="subtle"
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        AI
                      </Badge>
                    )}
                  </HStack>
                </MenuItemOption>
                <MenuItemOption
                  value="Nord"
                  color={
                    getAiResultForKey('location') === 'Nord'
                      ? 'purple.400'
                      : undefined
                  }
                >
                  <HStack justify="space-between" width="100%">
                    <Text>{t('protan:location.nord')}</Text>
                    {getAiResultForKey('location') === 'Nord' && (
                      <Badge
                        variant="subtle"
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        AI
                      </Badge>
                    )}
                  </HStack>
                </MenuItemOption>
                <MenuItemOption
                  value="Øst"
                  color={
                    getAiResultForKey('location') === 'Øst'
                      ? 'purple.400'
                      : undefined
                  }
                >
                  <HStack justify="space-between" width="100%">
                    <Text>{t('protan:location.ost')}</Text>
                    {getAiResultForKey('location') === 'Øst' && (
                      <Badge
                        variant="subtle"
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        AI
                      </Badge>
                    )}
                  </HStack>
                </MenuItemOption>
                <MenuItemOption
                  value="Vest"
                  color={
                    getAiResultForKey('location') === 'Vest'
                      ? 'purple.400'
                      : undefined
                  }
                >
                  <HStack justify="space-between" width="100%">
                    <Text>{t('protan:location.vest')}</Text>
                    {getAiResultForKey('location') === 'Vest' && (
                      <Badge
                        variant="subtle"
                        colorScheme="purple"
                        fontSize="xs"
                      >
                        AI
                      </Badge>
                    )}
                  </HStack>
                </MenuItemOption>
              </MenuOptionGroup>
            </MenuList>
          </Menu>
          {renderAiInfo('location')}
        </HStack>
        <HStack spacing={1} width="100%">
          <Text fontSize="xs" color="gray.300" minW={110} textAlign="right">
            {t('protan:label.building-height', 'Building height [m]')}
          </Text>
          <Input
            size="xs"
            variant="filled"
            placeholder={t(
              'protan:label.building-height',
              'Building height [m]'
            )}
            borderRadius={'md'}
            borderColor={borderColor}
            backgroundColor={borderColor}
            color={iconColor}
            _hover={{ backgroundColor: 'blackAlpha.500' }}
            value={roofOutlineData.buildingHeight || ''}
            onChange={(e) => {
              setRoofOutlineData((prev) => ({
                ...prev,
                buildingHeight: e.target.value,
              }));
              setManuallyChangedFields(
                (prev) => new Set([...prev, 'buildingHeight'])
              );
            }}
            flex={1}
          />
          {renderAiInfo('buildingHeight')}
        </HStack>
        <HStack spacing={1} width="100%">
          <Text fontSize="xs" color="gray.300" minW={110} textAlign="right">
            {t('protan:label.wind-load', 'Wind load (N/m²)')}
          </Text>
          <Input
            size="xs"
            variant="filled"
            placeholder={t('protan:label.wind-load', 'Wind load (N/m²)')}
            borderRadius={'md'}
            borderColor={borderColor}
            backgroundColor={borderColor}
            color={iconColor}
            _hover={{ backgroundColor: 'blackAlpha.500' }}
            value={roofOutlineData.windLoad}
            onChange={(e) => {
              setRoofOutlineData((prev) => ({
                ...prev,
                windLoad: e.target.value,
              }));
              setManuallyChangedFields(
                (prev) => new Set([...prev, 'windLoad'])
              );
            }}
            flex={1}
          />
          {renderAiInfo('windLoad')}
        </HStack>
      </VStack>
      <HStack spacing={1} marginTop={3}>
        <Text fontSize="xs" color="gray.300" minW={110} textAlign="right">
          Draw mode
        </Text>
        <Tooltip label={t('project:shapes.toolbar.rectangle')}>
          <Button
            variant={'ghost'}
            colorScheme={'teal'}
            _active={{ backgroundColor: activeBackgroundColor }}
            _hover={{
              backgroundColor:
                roofOutlineSubMode === DrawingMode.Rectangle
                  ? activeBackgroundColor
                  : 'blackAlpha.100',
            }}
            aria-label="draw rectangle"
            size="xs"
            leftIcon={
              <SparkelIcon
                as={RectangleIcon}
                color={
                  roofOutlineSubMode === DrawingMode.Rectangle
                    ? activeIconColor
                    : iconColor
                }
              />
            }
            isActive={roofOutlineSubMode === DrawingMode.Rectangle}
            onClick={() => {
              setRoofOutlineSubMode(DrawingMode.Rectangle);
              setDrawingMode(DrawingMode.Rectangle);
            }}
            style={
              roofOutlineSubMode === DrawingMode.Rectangle
                ? { backgroundColor: activeBackgroundColor }
                : undefined
            }
            color={
              roofOutlineSubMode === DrawingMode.Rectangle
                ? activeIconColor
                : iconColor
            }
          >
            {t('project:shapes.toolbar.rectangle')}
          </Button>
        </Tooltip>
        <Tooltip label={t('project:shapes.toolbar.polygon')}>
          <Button
            variant={'ghost'}
            colorScheme={'teal'}
            _active={{ backgroundColor: activeBackgroundColor }}
            _hover={{
              backgroundColor:
                roofOutlineSubMode === DrawingMode.Polygon
                  ? activeBackgroundColor
                  : 'blackAlpha.100',
            }}
            aria-label="draw polygon"
            size="xs"
            leftIcon={
              <SparkelIcon
                icon={faDrawPolygon}
                color={
                  roofOutlineSubMode === DrawingMode.Polygon
                    ? activeIconColor
                    : iconColor
                }
              />
            }
            isActive={roofOutlineSubMode === DrawingMode.Polygon}
            onClick={() => {
              setRoofOutlineSubMode(DrawingMode.Polygon);
              setDrawingMode(DrawingMode.Polygon);
            }}
            style={
              roofOutlineSubMode === DrawingMode.Polygon
                ? { backgroundColor: activeBackgroundColor }
                : undefined
            }
            color={
              roofOutlineSubMode === DrawingMode.Polygon
                ? activeIconColor
                : iconColor
            }
          >
            {t('project:shapes.toolbar.polygon')}
          </Button>
        </Tooltip>
        <Tooltip label={t('project:shapes.toolbar.magic-polygon')}>
          <Button
            variant={'ghost'}
            colorScheme={'teal'}
            _active={{ backgroundColor: activeBackgroundColor }}
            _hover={{
              backgroundColor:
                roofOutlineSubMode === DrawingMode.MagicPolygon
                  ? activeBackgroundColor
                  : 'blackAlpha.100',
            }}
            aria-label="draw magic polygon"
            size="xs"
            leftIcon={
              <SparkelIcon
                icon={faMagicWandSparkles}
                color={
                  roofOutlineSubMode === DrawingMode.MagicPolygon
                    ? activeIconColor
                    : iconColor
                }
              />
            }
            isActive={roofOutlineSubMode === DrawingMode.MagicPolygon}
            onClick={() => {
              setRoofOutlineSubMode(DrawingMode.MagicPolygon);
              setDrawingMode(DrawingMode.MagicPolygon);
            }}
            style={
              roofOutlineSubMode === DrawingMode.MagicPolygon
                ? { backgroundColor: activeBackgroundColor }
                : undefined
            }
            color={
              roofOutlineSubMode === DrawingMode.MagicPolygon
                ? activeIconColor
                : iconColor
            }
          >
            {t('project:shapes.toolbar.magic-polygon')}
          </Button>
        </Tooltip>
      </HStack>
      <Tooltip
        label={
          !isSaveDisabled
            ? t('project:shapes.save-tooltip')
            : t('project:shapes.save-disabled-tooltip')
        }
      >
        <Button
          aria-label="Save"
          size="sm"
          w="90%"
          marginTop={3}
          marginBottom={2}
          borderRadius={'md'}
          leftIcon={
            <SparkelIcon icon={faCheck} color={['gray.50', 'gray.700']} />
          }
          onClick={onSubmit}
          isLoading={isSaveLoading}
          isDisabled={isSaveDisabled}
        >
          Save
        </Button>
      </Tooltip>
    </VStack>
  );
}
